import { defineStore } from 'pinia'
import { api } from '@/api/client'
import type { ServerHealthBundle, VpnKey } from '@/types'

export interface HealthBar {
  color: string
  failed: number
  total: number
  timeRange: string
  partial: boolean
  current?: boolean
}

export interface ServerStatusEntry {
  status: string
  load_percent: number | null
  check_time: string | null
  uptime_percent?: number | null
}

export function emptyHealthBars(): HealthBar[] {
  return Array.from({ length: 24 }, () => ({
    color: 'gray',
    failed: 0,
    total: 0,
    timeRange: '',
    partial: false,
    current: false,
  }))
}

const PING_CACHE_KEY = 'server_ping_cache'

function readPingCache(): Record<string, number> {
  try {
    return JSON.parse(localStorage.getItem(PING_CACHE_KEY) || '{}')
  } catch {
    return {}
  }
}

function writePingCache(id: number, ms: number) {
  try {
    const cache = readPingCache()
    cache[String(id)] = ms
    localStorage.setItem(PING_CACHE_KEY, JSON.stringify(cache))
  } catch {
    /* ignore */
  }
}

export function collectHealthServerIds(keys: VpnKey[]): number[] {
  const ids = new Set<number>()
  for (const k of keys) {
    if (k.is_expired) continue
    if (k.display_server_id) ids.add(k.display_server_id)
    if (k.transit_server_id) ids.add(k.transit_server_id)
  }
  return [...ids]
}

export const useServerHealthStore = defineStore('serverHealth', {
  state: () => ({
    status: {} as Record<number, ServerStatusEntry>,
    history: {} as Record<number, HealthBar[]>,
    ping: {} as Record<number, number | null>,
    pingCache: readPingCache() as Record<string, number>,
    loading: false,
    _pollTimer: null as ReturnType<typeof setInterval> | null,
    _historyTimer: null as ReturnType<typeof setInterval> | null,
    _activeIds: [] as number[],
  }),

  getters: {
    // Бейдж показывает аптайм за 24ч, а не load_percent: «Online 6.0%» раньше
    // означало 6% нагрузки, но читалось как 6% доступности.
    onlineLabel(state) {
      return (serverId: number): string => {
        const s = state.status[serverId]
        if (!s) return 'Online —'
        if (s.status === 'online') {
          if (s.uptime_percent != null) return `Online ${s.uptime_percent.toFixed(1)}%`
          return 'Online'
        }
        if (s.status === 'offline') return 'Offline'
        return 'Unknown'
      }
    },
    onlineTitle(state) {
      return (serverId: number): string => {
        const s = state.status[serverId]
        if (!s) return 'Нет данных мониторинга'
        const parts: string[] = []
        if (s.uptime_percent != null) parts.push(`Доступность за 24ч: ${s.uptime_percent.toFixed(1)}%`)
        if (s.load_percent != null) parts.push(`Нагрузка сервера: ${s.load_percent.toFixed(1)}%`)
        if (s.check_time) parts.push(`Проверено: ${s.check_time}`)
        return parts.length ? parts.join('\n') : 'Нет данных мониторинга'
      }
    },
    onlineBadgeClass(state) {
      return (serverId: number): string => {
        const s = state.status[serverId]
        if (!s) return 'badge-secondary'
        if (s.status === 'offline') return 'badge-danger'
        if (s.status !== 'online') return 'badge-secondary'
        if (s.uptime_percent != null && s.uptime_percent < 95) return 'badge-warning'
        return 'badge-success'
      }
    },
    pingLabel(state) {
      return (serverId: number): string => {
        const ms = state.ping[serverId] ?? state.pingCache[String(serverId)] ?? null
        if (ms && ms > 0) return `Ping: ${Math.round(ms)}ms`
        return 'Ping: —ms'
      }
    },
    pingBadgeClass(state) {
      return (serverId: number): string => {
        const ms = state.ping[serverId] ?? state.pingCache[String(serverId)] ?? null
        if (ms && ms > 0) return 'badge-info'
        return 'badge-secondary'
      }
    },
    transitOnline(state) {
      return (transitId: number): boolean | null => {
        const s = state.status[transitId]
        if (!s) return null
        return s.status === 'online'
      }
    },
  },

  actions: {
    mergeBootstrap(bundle: ServerHealthBundle | undefined) {
      if (!bundle) return
      if (bundle.status) {
        for (const [k, v] of Object.entries(bundle.status)) {
          const id = Number(k)
          if (!Number.isNaN(id)) this.status[id] = v
        }
      }
      if (bundle.history) {
        for (const [k, v] of Object.entries(bundle.history)) {
          const id = Number(k)
          if (!Number.isNaN(id)) this.history[id] = v
        }
      }
      if (bundle.ping) {
        for (const [k, v] of Object.entries(bundle.ping)) {
          const id = Number(k)
          if (Number.isNaN(id)) continue
          let ms: number | null = null
          if (typeof v.ping_ms === 'number' && v.ping_ms > 0 && v.ping_ms < 10000) {
            ms = Math.round(v.ping_ms)
            writePingCache(id, ms)
            this.pingCache[String(id)] = ms
          }
          this.ping[id] = ms
        }
      }
    },

    setActiveIds(ids: number[]) {
      this._activeIds = [...new Set(ids)].sort((a, b) => a - b)
    },

    async refreshStatus(ids?: number[]) {
      const target = ids ?? this._activeIds
      if (target.length === 0) return
      this.loading = true
      try {
        const { data } = await api.get<{ success: boolean; data: Record<string, ServerStatusEntry> }>(
          '/api/server-status.php',
          { params: { server_ids: target.join(',') } },
        )
        if (data.success && data.data) {
          for (const [k, v] of Object.entries(data.data)) {
            const id = Number(k)
            if (!Number.isNaN(id)) this.status[id] = v
          }
        }
        await this.refreshPing(target)
      } catch {
        /* ignore transient health errors */
      } finally {
        this.loading = false
      }
    },

    async refreshHistory(ids?: number[]) {
      const target = ids ?? this._activeIds
      if (target.length === 0) return
      try {
        const { data } = await api.get<{ success: boolean; data: Record<string, HealthBar[]> }>(
          '/api/health-history.php',
          { params: { server_ids: target.join(',') } },
        )
        if (data.success && data.data) {
          for (const [k, v] of Object.entries(data.data)) {
            const id = Number(k)
            if (!Number.isNaN(id)) this.history[id] = v
          }
        }
      } catch {
        /* ignore */
      }
    },

    async refreshPing(ids: number[]) {
      if (ids.length === 0) return
      try {
        const { data } = await api.get<{ success: boolean; data: Record<string, { success: boolean; ping_ms: number }> }>(
          '/api/ping.php',
          { params: { server_ids: ids.join(',') } },
        )
        if (!data.success || !data.data) return
        for (const [k, v] of Object.entries(data.data)) {
          const id = Number(k)
          if (Number.isNaN(id)) continue
          let ms: number | null = null
          if (typeof v.ping_ms === 'number' && v.ping_ms > 0 && v.ping_ms < 10000) {
            ms = Math.round(v.ping_ms)
            writePingCache(id, ms)
            this.pingCache[String(id)] = ms
          } else {
            const cached = this.pingCache[String(id)]
            ms = cached && cached > 0 ? cached : null
          }
          this.ping[id] = ms
        }
      } catch {
        /* ignore */
      }
    },

    startPolling(ids: number[]) {
      this.stopPolling()
      this.setActiveIds(ids)
      if (ids.length === 0) return
      void this.refreshStatus()
      void this.refreshHistory()
      this._pollTimer = setInterval(() => void this.refreshStatus(), 60_000)
      this._historyTimer = setInterval(() => void this.refreshHistory(), 5 * 60_000)
    },

    stopPolling() {
      if (this._pollTimer) clearInterval(this._pollTimer)
      if (this._historyTimer) clearInterval(this._historyTimer)
      this._pollTimer = null
      this._historyTimer = null
    },
  },
})
